// /en/work-permit/$nationality — EN per-nationality detail
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getWpNationality, WP_NATIONALITIES } from "@/data/work-permit-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Briefcase, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, Clock, Wallet, Building2, FileText, Languages } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/work-permit/$nationality")({
  loader: ({ params }) => {
    const n = getWpNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getWpNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Nationality not found", "/en/work-permit/$nationality", "en")}] };
    const url = `${SITE}/en/work-permit/${n.slug}`;
    const title = `Thailand Work Permit for ${n.demonymEn} Nationals ${n.flag} · BOI Fast-Track ${n.fastTrackDays} Days | NYC`;
    const desc = `Thailand Work Permit + Non-B Visa for ${n.demonymEn} (${n.countryEn}) nationals — min salary · BOI/EEC ${n.fastTrackDays} days · Standard MOL ${n.standardDays} days · Includes translation, MFA, family dependent visas., 99.6% acceptance. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/work-permit/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/work-permit/$nationality", "en")},
        { name: "keywords", content: n.keywordsEn.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/work-permit/$nationality", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/work-permit/$nationality", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/work-permit/${n.slug}`, `/en/work-permit/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `Thailand Work Permit + Non-B Visa for ${n.demonymEn} nationals`,
              serviceType: "Work Permit Processing",
              description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "Audience", audienceType: n.demonymEn },
              offers: { "@type": "Offer", price: "12000", priceCurrency: "THB", description: "Work Permit + Non-B + dependent visa" },
            }),
          },
          howToSchema({
            name: `How to obtain a Thailand Work Permit for ${n.demonymEn} nationals`,
            description: `Country-specific procedure for ${n.countryEn} passport holders`,
            totalTime: `P${n.standardDays}D`,
            estimatedCost: { currency: "THB", value: "12000" },
            inLanguage: "en",
            steps: [
              { name: `Collect source documents in ${n.countryEn}`, text: `Degree, employment letter, marriage, birth certificates — authenticate at ${n.consulates[0]}` },
              { name: "Translate by MOJ-registered translator", text: `Translate ${n.demonymEn} → Thai (and EN if needed) by MOJ translator + MFA legalisation`, url: `${SITE}/en/translate` },
              { name: "Apply for Non-B Visa", text: `Submit at ${n.consulates[0]} — 5-7 business days` },
              { name: "Enter Thailand and file Work Permit", text: `${n.fastTrackDays} days (BOI/EEC) or ${n.standardDays} days (MOL) — min salary THB ${n.minSalaryTHB.toLocaleString()}/month` },
              { name: "Dependent + Re-Entry", text: "Apply Non-B Dependent for spouse/children + Multiple Re-Entry THB 3,800" },
            ],
          }),
          faqPageSchema([
            { q: `How long does Work Permit processing take for ${n.demonymEn} nationals?`, a: `BOI/EEC fast-track ${n.fastTrackDays} days · Standard MOL ${n.standardDays} days` },
            { q: `What is the minimum salary for ${n.demonymEn} nationals?`, a: `THB ${n.minSalaryTHB.toLocaleString()}/month per Ministry of Labour schedule.` },
            { q: `Which industries hire the most ${n.demonymEn} expats?`, a: n.topIndustries.join(" · ") },
            { q: `Where can I apply for the Non-B visa?`, a: n.consulates.join(" · ") },
            { q: `How are source documents legalised?`, a: n.noteEn },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Work Permit", item: `${SITE}/en/work-permit` },
                { "@type": "ListItem", position: 3, name: `${n.demonymEn}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: WpDetailEn,
});

function WpDetailEn() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getWpNationality(slug)!;
  const others = WP_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link><span className="mx-2">/</span>
            <Link to="/en/work-permit" className="hover:text-primary">Work Permit</Link><span className="mx-2">/</span>
            <span>{n.demonymEn}</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Briefcase className="mr-1 inline h-3 w-3" /> Work Permit · {n.countryEn}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>Thailand Work Permit for {n.demonymEn} Nationals
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground sm:text-lg">{n.leadEn}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How fast can {n.demonymEn} nationals get a Thai Work Permit?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {n.demonymEn} nationals receive a Thai Work Permit + Non-B Visa in {n.fastTrackDays} days (BOI/EEC fast-track) or {n.standardDays} days (MOL standard). Minimum salary THB {n.minSalaryTHB.toLocaleString()}/month. NYC processed with 99.6% first-time acceptance.
            </p>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-4 max-w-3xl text-sm">
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">BOI</div><div className="font-bold">{n.fastTrackDays}d</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">MOL</div><div className="font-bold">{n.standardDays}d</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">Min salary</div><div className="font-bold">฿{(n.minSalaryTHB / 1000).toFixed(0)}k</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">NYC fee</div><div className="font-bold">฿12,000</div></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 space-y-12">
        <section>
          <h2 className="text-2xl font-extrabold"><Building2 className="mr-2 inline h-6 w-6 text-primary" />Top industries hiring {n.demonymEn} professionals</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {n.topIndustries.map((i) => (<li key={i} className="rounded-xl border border-border bg-card p-3 text-sm">• {i}</li>))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><FileText className="mr-2 inline h-6 w-6 text-primary" />Thai embassies/consulates for Non-B issuance</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {n.consulates.map((c) => (<li key={c} className="rounded-xl border border-border bg-card p-3 text-sm">📍 {c}</li>))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><Languages className="mr-2 inline h-6 w-6 text-primary" />Country-specific guidance</h2>
          <p className="mt-4 rounded-2xl border-l-4 border-primary bg-primary/5 p-4 text-sm leading-relaxed">{n.noteEn}</p>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold">Key Facts</h2>
          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-border">
              {[
                ["Nationality", `${n.flag} ${n.countryEn} (${n.demonymEn})`],
                ["BOI/EEC Fast-Track", `${n.fastTrackDays} business days`],
                ["MOL Standard", `${n.standardDays} business days`],
                ["Minimum salary", `THB ${n.minSalaryTHB.toLocaleString()}/month`],
                ["Government fee", "THB 3,000/year"],
                ["NYC service fee", "THB 12,000 (filing + translation + dependent)"],
                ["Dependent Visa", "Spouse + children <20 yo (free Non-B Dependent)"],
                ["Re-Entry Permit", "Multiple THB 3,800"],
              ].map(([k, v]) => (<tr key={k}><td className="py-2 pr-4 font-semibold">{k}</td><td className="py-2">{v}</td></tr>))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Companion services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Link to="/en/visa/$nationality" params={{ nationality: n.slug }} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">All Thai visa types for {n.demonymEn}</div>
              <div className="mt-1 text-xs text-muted-foreground">Non-B / LTR / DTV / Smart / Elite / Retirement / Marriage</div>
            </Link>
            <Link to="/en/industrial-estates" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">20+ Industrial Estates</div>
              <div className="mt-1 text-xs text-muted-foreground">EEC · Amata · WHA · Lat Krabang · Rojana</div>
            </Link>
            <Link to="/en/translate" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Certified translation + MFA</div>
              <div className="mt-1 text-xs text-muted-foreground">ISO 17100 · from THB 800/page</div>
            </Link>
            <Link to="/en/apostille" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Apostille (Hague)</div>
              <div className="mt-1 text-xs text-muted-foreground">60+ Hague countries</div>
            </Link>
          </div>
        </section>

        {others.length > 0 && (
          <section className="border-t border-border pt-10">
            <h2 className="text-2xl font-extrabold">Nearby nationalities in the same region</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/en/work-permit/$nationality" params={{ nationality: o.slug }} className="group rounded-2xl border border-border bg-card p-4 hover:border-primary">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{o.flag}</div>
                    <div className="font-bold">{o.demonymEn}</div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">BOI {o.fastTrackDays}d · MOL {o.standardDays}d</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">View Work Permit <ArrowRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
